import { BaseCatalogItemTransformer } from './baseCatalogItem.transformer';
import type { Publication } from '@/types';
import type { CatalogType } from '../services/catalog/catalog-types';
import { getMediaData } from '@/lib/utils/media.utils';
import { processTagArray, getYearTag, getRelationTag, combineTags } from '@/lib/utils/tagGenerator';

/**
 * Transformer for converting Publication entities to CatalogItem
 */
export class WritingTransformer extends BaseCatalogItemTransformer<Publication> {
  protected getId(publication: Publication): string | number {
    return publication.id.toString();
  }

  protected getType(_publication: Publication): CatalogType {
    return 'writing';
  }

  protected getSlug(publication: Publication): string {
    return publication.slug;
  }

  protected getHref(publication: Publication): string {
    return `/catalog/writings/${publication.slug}`;
  }

  protected getTitle(publication: Publication): string {
    return publication.title;
  }

  protected getDisplayTitle(publication: Publication): string {
    return publication.title;
  }

  protected getFilename(publication: Publication): string {
    return publication.slug;
  }

  protected getThumbnail(publication: Publication): string {
    const cardImageData = getMediaData(publication.image);
    return cardImageData.url || '';
  }

  protected getImageUrl(publication: Publication): string {
    return this.getThumbnail(publication);
  }

  protected getWidth(publication: Publication): number {
    // Use meta.image for dimensions if available, otherwise use default
    if (publication.meta && typeof publication.meta.image !== 'number') {
      const metaImageData = getMediaData(publication.meta.image);
      return metaImageData.width || 800;
    }
    return 800;
  }

  protected getHeight(publication: Publication): number {
    // Use meta.image for dimensions if available, otherwise use default
    if (publication.meta && typeof publication.meta.image !== 'number') {
      const metaImageData = getMediaData(publication.meta.image);
      return metaImageData.height || 600;
    }
    return 600;
  }

  protected getDate(publication: Publication): string {
    return publication.publishDate || publication.createdAt;
  }

  protected getYear(publication: Publication): number {
    if (publication.publishDate) {
      return new Date(publication.publishDate).getFullYear();
    }
    return 0;
  }

  protected getDescription(publication: Publication): string {
    return publication.description;
  }

  protected getLongDescription(publication: Publication): string {
    return publication.description;
  }

  protected getDoi(publication: Publication): string | undefined {
    return publication.doi || undefined;
  }

  protected getAuthors(publication: Publication): string[] | undefined {
    if (publication.authors && Array.isArray(publication.authors)) {
      return publication.authors.map(author => {
        if (typeof author === 'object' && 'name' in author) {
          return author.name;
        }
        return '';
      }).filter(name => name);
    }
    return undefined;
  }

  protected getPublishedIn(publication: Publication): string | undefined {
    if (publication.publishedIn && typeof publication.publishedIn === 'object' && 'title' in publication.publishedIn) {
      return publication.publishedIn.title;
    }
    return undefined;
  }

  protected getPdfUrl(publication: Publication): string | undefined {
    if (publication.pdfFile && typeof publication.pdfFile === 'object' && 'url' in publication.pdfFile) {
      return publication.pdfFile.url;
    }
    return undefined;
  }

  protected getOriginalLink(publication: Publication): string | undefined {
    return publication.originalLink || undefined;
  }

  protected getTags(publication: Publication): string[] {
    const yearTag = getYearTag(publication.publishDate);
    return combineTags(
      processTagArray(publication.tags),
      getRelationTag(publication.category),
      yearTag && parseInt(yearTag) > 0 ? yearTag : null
    );
  }
}

// Export singleton instance
export const writingTransformer = new WritingTransformer();